/**
 * SCRUM-207 — Tenant inbox: consumer requests (arrepentimiento / baja de servicio).
 * Listado, cambio de estado y notas de resolución. Gate: can_manage_orders.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { CrudEmptyState, CrudShellHeader, Loading } from "@/components/ds";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ClipboardList,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import type {
  ConsumerRequest,
  ConsumerRequestStatus,
  ConsumerRequestType,
} from "@/lib/db-service/consumer-requests";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";

export const Route = createFileRoute("/app/cumplimiento/solicitudes")({
  component: SolicitudesPage,
  head: () => ({ meta: [{ title: "Solicitudes de consumidor" }] }),
});

type ListResult = { items: ConsumerRequest[]; total: number };

const STATUS_LABELS: Record<ConsumerRequestStatus, string> = {
  SOLICITADA: "Solicitada",
  RECIBIDA: "Recibida",
  EN_GESTION: "En gestión",
  RESUELTA: "Resuelta",
  RECHAZADA: "Rechazada",
};

const STATUS_COLORS: Record<ConsumerRequestStatus, string> = {
  SOLICITADA: "bg-yellow-100 text-yellow-800",
  RECIBIDA: "bg-blue-100 text-blue-800",
  EN_GESTION: "bg-purple-100 text-purple-800",
  RESUELTA: "bg-green-100 text-green-800",
  RECHAZADA: "bg-red-100 text-red-800",
};

const TYPE_LABELS: Record<ConsumerRequestType, string> = {
  ARREPENTIMIENTO: "Arrepentimiento",
  BAJA_SERVICIO: "Baja de servicio",
};

const PAGE_SIZE = 20;

function SolicitudesPage() {
  const tid = useWorkspaceTenantId();
  const [items, setItems] = useState<ConsumerRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [selected, setSelected] = useState<ConsumerRequest | null>(null);

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (typeFilter !== "all") params.set("type", typeFilter);
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(page * PAGE_SIZE));
      const data = await apiGetJson<ListResult>(
        `/api/consumer-requests?${params}`,
        tid,
      );
      setItems(data.items);
      setTotal(data.total);
    } catch {
      toast.error("Error al cargar solicitudes");
    } finally {
      setLoading(false);
    }
  }, [tid, page, statusFilter, typeFilter]);

  useEffect(() => { void load(); }, [load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (loading && items.length === 0) return <Loading />;

  return (
    <div className="space-y-4 p-4 md:p-6">
      <CrudShellHeader
        title="Solicitudes de consumidor"
        subtitle="Arrepentimiento y baja de servicio (Disp. 954/2025 + 3/2026)"
      />
      <MerchantExperienceBar screenId="cumplimiento-solicitudes" />

      <div className="flex flex-wrap items-center gap-3">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="SOLICITADA">Solicitada</SelectItem>
            <SelectItem value="RECIBIDA">Recibida</SelectItem>
            <SelectItem value="EN_GESTION">En gestión</SelectItem>
            <SelectItem value="RESUELTA">Resuelta</SelectItem>
            <SelectItem value="RECHAZADA">Rechazada</SelectItem>
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(0); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="ARREPENTIMIENTO">Arrepentimiento</SelectItem>
            <SelectItem value="BAJA_SERVICIO">Baja de servicio</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </Button>

        <span className="ml-auto text-sm text-muted-foreground">
          {total} solicitud{total !== 1 ? "es" : ""}
        </span>
      </div>

      {items.length === 0 && !loading ? (
        <CrudEmptyState
          icon={ClipboardList}
          title="Sin solicitudes"
          description="Las solicitudes de arrepentimiento y baja de servicio aparecerán acá."
        />
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <Card
              key={item.id}
              className={`cursor-pointer transition-shadow hover:shadow-md ${selected?.id === item.id ? "ring-2 ring-primary" : ""}`}
              onClick={() => setSelected(selected?.id === item.id ? null : item)}
            >
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-bold tracking-wider">
                    {item.referenceCode}
                  </span>
                  <Badge className={STATUS_COLORS[item.status]}>
                    {STATUS_LABELS[item.status]}
                  </Badge>
                  <Badge variant="outline">{TYPE_LABELS[item.type]}</Badge>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {new Date(item.createdAt).toLocaleDateString("es-AR", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="pt-0 text-sm">
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-muted-foreground">
                  <span><strong>Nombre:</strong> {item.requesterName}</span>
                  <span><strong>Email:</strong> {item.requesterEmail}</span>
                  {item.requesterPhone ? <span><strong>Tel:</strong> {item.requesterPhone}</span> : null}
                  {item.orderNumber ? <span><strong>Pedido:</strong> #{item.orderNumber}</span> : null}
                </div>
                {item.reason ? (
                  <p className="mt-2 text-sm text-muted-foreground">
                    <strong>Motivo:</strong> {item.reason}
                  </p>
                ) : null}

                {selected?.id === item.id ? (
                  <RequestActions item={item} tid={tid!} onUpdated={load} />
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 ? (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Página {page + 1} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function RequestActions({
  item,
  tid,
  onUpdated,
}: {
  item: ConsumerRequest;
  tid: string;
  onUpdated: () => void;
}) {
  const [newStatus, setNewStatus] = useState<string>("");
  const [notes, setNotes] = useState(item.resolutionNotes ?? "");
  const [saving, setSaving] = useState(false);

  const terminal = item.status === "RESUELTA" || item.status === "RECHAZADA";

  async function handleSave() {
    if (!newStatus && notes === (item.resolutionNotes ?? "")) return;
    setSaving(true);
    try {
      await apiSendJson(
        "/api/consumer-requests",
        "PATCH",
        {
          id: item.id,
          status: newStatus || item.status,
          resolutionNotes: notes || undefined,
        },
        tid,
      );
      toast.success("Solicitud actualizada");
      onUpdated();
    } catch {
      toast.error("Error al actualizar");
    } finally {
      setSaving(false);
    }
  }

  const nextStatuses: string[] = [];
  if (!terminal) {
    const transitions: Record<string, string[]> = {
      SOLICITADA: ["RECIBIDA", "EN_GESTION", "RECHAZADA"],
      RECIBIDA: ["EN_GESTION", "RESUELTA", "RECHAZADA"],
      EN_GESTION: ["RESUELTA", "RECHAZADA"],
    };
    nextStatuses.push(...(transitions[item.status] ?? []));
  }

  return (
    <div className="mt-4 space-y-3 rounded-lg border bg-muted/30 p-3">
      {item.resolutionNotes && terminal ? (
        <p className="text-sm">
          <strong>Notas de resolución:</strong> {item.resolutionNotes}
        </p>
      ) : null}

      {!terminal ? (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium">Cambiar estado</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Seleccionar estado…" />
                </SelectTrigger>
                <SelectContent>
                  {nextStatuses.map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_LABELS[s as ConsumerRequestStatus]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium">Notas de resolución</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              rows={2}
              placeholder="Agregar notas internas o resolución…"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </div>

          <Button size="sm" onClick={handleSave} disabled={saving || (!newStatus && notes === (item.resolutionNotes ?? ""))}>
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Guardar cambios
          </Button>
        </>
      ) : null}
    </div>
  );
}
